import { orange } from '@mui/material/colors'
import { keyBy } from 'lodash'
import React from 'react'
import { useSelector } from 'react-redux'

import { selectAllRoutesMetaData } from 'slices/configuredService/configuredServiceScheduleSlice'

interface RouteIconProps {
  routes?: Array<{ routeId?: string; color?: string }>
  size?: number
  borderWidth?: number
  borderColor: string
  unassignedColor?: string
  // routeId → colour for callers that already know the palette; without it the store's routes metadata decides.
  routeColorMap?: Record<string, string>
}

// No routes means a full circle in the unassigned colour, not an empty icon.
const RouteIcon = ({
  routes = [],
  size = 20,
  borderWidth = 1,
  borderColor,
  unassignedColor = orange[300],
  routeColorMap,
}: RouteIconProps) => {
  const routesMeta = useSelector(selectAllRoutesMetaData) || []
  const routesMetaById = keyBy(routesMeta, 'id')

  const radius = size / 2
  const innerRadius = radius - borderWidth

  const colors = routes.map(route => {
    if (routeColorMap) return (route.routeId && routeColorMap[route.routeId]) || route.color || unassignedColor
    return (route.routeId && routesMetaById[route.routeId]?.color) || route.color || unassignedColor
  })

  if (routes.length === 0) {
    return (
      <svg height={size} overflow="visible" viewBox={`0 0 ${size} ${size}`} width={size}>
        <circle cx={radius} cy={radius} fill={unassignedColor} r={radius} stroke={borderColor} strokeWidth={borderWidth} />
      </svg>
    )
  }

  if (colors.length === 1) {
    const color = colors[0]
    return (
      <svg height={size} overflow="visible" viewBox={`0 0 ${size} ${size}`} width={size}>
        <circle cx={radius} cy={radius} fill={color} r={radius} stroke={borderColor} strokeWidth={borderWidth} />
      </svg>
    )
  }

  const sliceAngle = 360 / colors.length

  const slices = colors.map((color, index) => {
    const startAngle = index * sliceAngle
    const endAngle = (index + 1) * sliceAngle

    const startRad = ((startAngle - 90) * Math.PI) / 180
    const endRad = ((endAngle - 90) * Math.PI) / 180

    const x1 = radius + innerRadius * Math.cos(startRad)
    const y1 = radius + innerRadius * Math.sin(startRad)
    const x2 = radius + innerRadius * Math.cos(endRad)
    const y2 = radius + innerRadius * Math.sin(endRad)

    const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1

    const pathData = [
      `M ${radius},${radius}`,
      `L ${x1},${y1}`,
      `A ${innerRadius},${innerRadius} 0 ${largeArcFlag} 1 ${x2},${y2}`,
      'Z',
    ].join(' ')

    // Index is a colour-only slice's only identity — two routes sharing a colour would collide on the key.
    // eslint-disable-next-line react/no-array-index-key
    return <path key={index} d={pathData} fill={color} />
  })

  return (
    <svg height={size} overflow="visible" viewBox={`0 0 ${size} ${size}`} width={size}>
      <circle cx={radius} cy={radius} fill={borderColor} r={radius} />
      {slices}
    </svg>
  )
}

export default RouteIcon
